class YhsQuickAdd extends HTMLElement {
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #resetTimeout;

  connectedCallback() {
    this.trigger = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-yhs-quick-add-trigger]'));
    this.panel = this.querySelector('[data-yhs-quick-add-panel]');
    this.closeButton = this.querySelector('[data-yhs-quick-add-close]');
    this.sizeButtons = Array.from(this.querySelectorAll('[data-yhs-quick-add-size]'));
    this.singleVariantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-yhs-quick-add-single-variant]')
    );

    const { signal } = this.#abortController;

    this.trigger?.addEventListener('click', this.#onTriggerClick, { signal });
    this.closeButton?.addEventListener('click', this.#onCloseClick, { signal });
    this.sizeButtons.forEach((button) => button.addEventListener('click', this.#onSizeClick, { signal }));
    document.addEventListener('click', this.#onDocumentClick, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    window.clearTimeout(this.#resetTimeout);
  }

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.panel) {
      if (this.panel.hasAttribute('hidden')) this.#openPanel();
      else this.#closePanel();
      return;
    }

    if (this.singleVariantInput?.value) {
      this.#addToCart(this.singleVariantInput.value);
    }
  };

  /** @param {MouseEvent} event */
  #onCloseClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#closePanel();
  };

  #openPanel = () => {
    this.panel?.removeAttribute('hidden');
    this.classList.add('is-open');
  };

  #closePanel = () => {
    this.panel?.setAttribute('hidden', '');
    this.classList.remove('is-open');
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closePanel();
  };

  /** @param {MouseEvent} event */
  #onSizeClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#addToCart(variantId);
  };

  /** @param {string} variantId */
  async #addToCart(variantId) {
    if (this.classList.contains('is-loading')) return;
    this.classList.add('is-loading');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .filter((item) => item instanceof HTMLElement && item.dataset.sectionId)
      .map((item) => /** @type {HTMLElement} */ (item).dataset.sectionId);

    const url = /** @type {any} */ (window).Theme?.routes?.cart_add_url || '/cart/add.js';

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();
      this.classList.remove('is-loading');

      if (data.status) {
        this.classList.add('is-error');
        window.clearTimeout(this.#resetTimeout);
        this.#resetTimeout = window.setTimeout(() => this.classList.remove('is-error'), 1500);

        document.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              sourceId: this.dataset.productId,
              data: {
                message: data.message,
                errors: data.errors,
                description: data.description,
              },
            },
          })
        );
        return;
      }

      this.#closePanel();
      this.classList.add('is-added');
      window.clearTimeout(this.#resetTimeout);
      this.#resetTimeout = window.setTimeout(() => this.classList.remove('is-added'), 1600);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: this.dataset.productId,
            data: {
              source: 'product-form-component',
              itemCount: 1,
              productId: this.dataset.productId,
              sections: data.sections,
            },
          },
        })
      );
    } catch (error) {
      this.classList.remove('is-loading');
      console.error(error);
    }
  }
}

if (!customElements.get('yhs-quick-add')) {
  customElements.define('yhs-quick-add', YhsQuickAdd);
}
